<script>
import every from 'lodash/every'
import values from 'lodash/values'
import {
  measureListComputed,
  measureListMethods,
} from '@state/helpers/measure-list'

import SearchPayload from '@utils/searchPayload'
import { ObjectHelper, csvExport, addParamsToLocation } from '@utils/helpers'
import cloneDeep from 'lodash/cloneDeep'
import CsvExportPopup from '@components/molecules/csv-export-popup.vue'
import { endOfDay, startOfDay } from 'date-fns'
import { CSV_FILE_NAME } from '@utils/csvFilename'
import { DATE_TIME_FORMAT } from '@/src/utils/constant'
import formatDate from '@utils/format-date'
import fieldsBoxCard from './fieldsBoxCard'
import tableFields from './tableFields'

export default {
  components: { CsvExportPopup },
  page: {
    title: 'Measure List',
    meta: [{ name: 'description', content: 'The Measure List page.' }],
  },
  data() {
    return {
      items: {},
      searchBody: new SearchPayload.Builder()
        .withSearchUsername()
        .withEmail()
        .build(),
      fields: tableFields,
      filtered: false,
      openConfirm: false,
      measureIdSelected: {},
      fieldsBoxCard: fieldsBoxCard,
      componentKey: 0,
      csvKey: 0,
    }
  },
  computed: {
    ...measureListComputed,
    measureListData() {
      const list =
        cloneDeep(this.getMeasureList?.list)?.map((item) => {
          item.measure_id = item.id
          item.weight = item.data.weight + 'kg'
          item.BMI = item.data.BMI
          item.body_fat_rate =
            parseFloat(item.data.body_fat_rate).toFixed(1) + '%'
          item.athlete = !item.athlete ? 'いいえ' : 'はい'
          return item
        }) || []
      return list
    },
    paginationComputed() {
      let paginations = {}
      if (this.getMeasureList) {
        paginations = ObjectHelper.removePropertyByKey('list')(
          this.getMeasureList
        )
      }
      return paginations
    },
    unselectedAll() {
      return every(values(this.measureIdSelected), function(v) {
        return !v
      })
    },
    confirmComputed() {
      if (this.unselectedAll) {
        return 'unselected'
      }
      return 'delete'
    },
  },
  watch: {
    searchBody() {
      addParamsToLocation(this.$route, this.searchBody)
      this.componentKey++
    },
  },
  created() {
    const filter = this.$route.query
    filter.page_no = parseInt(filter.page_no, 10)
    filter.lines = parseInt(filter.lines, 10)
    for (const key in filter) {
      if (key !== 'page_no' && key !== 'lines') {
        if (filter[key] !== '' && filter[key] !== undefined) {
          this.searchBody = filter
          this.filtered = true
        }
      }
    }
    if (filter.page_no > 1) {
      this.searchBody = filter
    }
    this.handleSearchMeasure()
  },
  methods: {
    ...measureListMethods,
    showModal() {
      this.openConfirm = !this.openConfirm
    },
    handlePagination(body) {
      this.searchBody = { ...this.searchBody, ...body }
      this.handleSearchMeasure()
    },
    handleSorter(key) {
      this.searchBody = { ...this.searchBody, order_by: key }
      this.handleSearchMeasure()
    },
    handleFilter(params, reset = false) {
      this.searchBody = { ...this.searchBody, ...params }
      this.handleSearchMeasure()
      this.filtered = !reset
    },
    handleSearchMeasure() {
      this.fetchingMeasureList(this.searchBody)
    },

    handleSelected(id, value) {
      const temp = { ...this.measureIdSelected }
      temp[id] = value
      this.measureIdSelected = { ...temp }
    },
    viewDetail(id) {
      this.$router.push({
        path: `/panel/measure-list/${id}`,
      })
    },
    submit() {
      const temp = { ...this.measureIdSelected }
      const flag = Object.keys(temp)?.map((key) => {
        if (temp[key]) {
          return key
        }
      })
      Promise.all(
        flag?.map((id) => {
          return this.deleteMeasureById({ id })
        })
      ).then((rs) => {
        this.openConfirm = !this.openConfirm
        this.fetchingMeasureList(this.searchBody)
        this.measureIdSelected = {}
      })
    },
    async exportCsv(date) {
      date.date_end = formatDate(
        endOfDay(new Date(date.date_end)),
        DATE_TIME_FORMAT
      )
      date.date_start = formatDate(
        startOfDay(new Date(date.date_start)),
        DATE_TIME_FORMAT
      )
      const response = await this.exportMeasureCsv({
        ...this.searchBody,
        ...date,
      })

      csvExport(response, CSV_FILE_NAME.measureList)
      this.csvKey++
    },
  },
}
</script>

<template>
  <div>
    <BaseFilterBoxCard
      :key="componentKey"
      :filter-value="searchBody"
      :fields="fieldsBoxCard"
      @filter="(p, r) => handleFilter(p, r)"
    />
    <BasePagination
      :paginations="paginationComputed"
      :searching="filtered"
      @activePagination="handlePagination"
    >
      <template v-slot:secondary>
        <CButton
          color="dark"
          variant="outline"
          class="py-0 ml-1"
          @click="showModal"
          >削除</CButton
        >
        <CsvExportPopup
          :key="csvKey"
          :label="$t('common.measure_date')"
          @export="exportCsv"
        />
      </template>
    </BasePagination>
    <BaseTable
      striped
      border
      hover
      :table-fields="fields"
      :data-tables="measureListData"
      @sorterBy="handleSorter"
      @row-clicked="(item) => viewDetail(item.measure_id)"
    >
      <template v-slot:checkbox="{ item }">
        <td class="py-2" @click.stop="() => {}">
          <CInputCheckbox
            :checked="measureIdSelected[item.measure_id]"
            color="primary"
            @update:checked="(v) => handleSelected(item.measure_id, v)"
          />
        </td>
      </template>
      <template v-slot:created_at="{ item }">
        <td>
          {{ item.created_at }}
        </td>
      </template>
    </BaseTable>
    <BaseModalConfirm
      backdrop-close
      :show.sync="openConfirm"
      :type="confirmComputed"
      :hide-action-button="unselectedAll"
      @closeModal="openConfirm = !openConfirm"
      @submit="submit"
    />
  </div>
</template>
